import { type EnvironmentProviders, InjectionToken, inject, makeEnvironmentProviders } from '@angular/core';

import { getApp, getApps, initializeApp } from 'firebase/app';
import { type Messaging, getMessaging } from 'firebase/messaging';

import { FinEnvironmentService } from '@finverity/platform-app/core/services';

/**
 * The Firebase Cloud Messaging instance of this tab.
 *
 * Resolved lazily on the first injector lookup, because `getMessaging` may only run once
 * `isSupported()` has answered — which is why {@link FirebaseCloudMessagingService} asks the
 * injector for this token instead of taking it in its constructor.
 */
export const FIREBASE_MESSAGING = new InjectionToken<Messaging>('FIREBASE_MESSAGING');

/**
 * Registers the Firebase app from the tenant's own configuration and its messaging instance.
 *
 * Left out of the application config entirely for air-gap deployments, where FCM cannot reach
 * Google's servers.
 *
 * @returns The providers to add to the application config.
 */
export function provideFirebaseMessaging(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: FIREBASE_MESSAGING,
      useFactory: (): Messaging => {
        const environmentService = inject(FinEnvironmentService);
        const app = getApps().length > 0 ? getApp() : initializeApp(environmentService.firebase);
        return getMessaging(app);
      },
    },
  ]);
}
